import { useEffect } from 'react';
import { isProxyableMediaFileName, NxObjectType, ProxyPlayback } from '@warehouse/object-browser/core';
import { objectBrowserStoreSelector, useObjectBrowserStore } from '@warehouse/object-browser/domain';
import {
	BrokerJobStatus,
	brokerJobsStoreSelector,
	MEZZ_PROXY_SERVICE_UUID,
	useBrokerJobsStore,
} from '@warehouse/object-browser/feature-broker';

/** The broker jobs are not polled on their own, so a running Mezz Proxy job is re-read at this pace. */
const RUNNING_JOB_POLL_INTERVAL_MS = 5000;

const SETTLED_JOB_STATUSES: BrokerJobStatus[] = ['SUCCEEDED', 'FAILED', 'CANCELLED'];

/**
 * The selected object's proxy, as the Inventory API returns it in `metadata.proxy`. When there is none,
 * the latest Mezz Proxy job says why: still running, failed, or never run.
 */
export function useProxyPlayback(): ProxyPlayback | undefined {
	const nxObjectFull = useObjectBrowserStore(objectBrowserStoreSelector.selectedNxObjectFull);
	const nxObjectUuid = nxObjectFull?.nxObject.uuid ?? '';
	const jobs = useBrokerJobsStore(brokerJobsStoreSelector.jobsFor(nxObjectUuid));
	const areJobsLoading = useBrokerJobsStore(brokerJobsStoreSelector.isLoading(nxObjectUuid));
	const { refresh } = useBrokerJobsStore(brokerJobsStoreSelector.actions);
	const { subscribe, unsubscribe } = useBrokerJobsStore(brokerJobsStoreSelector.internalActions);
	// The broker answers newest first, so the first Mezz Proxy job is the latest one.
	const latestProxyJob = jobs.find((job) => job.serviceUUID === MEZZ_PROXY_SERVICE_UUID);
	const isProxyJobRunning = !!latestProxyJob && !SETTLED_JOB_STATUSES.includes(latestProxyJob.status);

	const isPlayableMediaFile =
		nxObjectFull?.nxObject.itemType === NxObjectType.File && isProxyableMediaFileName(nxObjectFull.nxObject.name);

	// Nothing else may be watching this object's jobs while the General tab is open.
	useEffect(() => {
		if (!isPlayableMediaFile) return undefined;
		subscribe(nxObjectUuid);
		return () => unsubscribe(nxObjectUuid);
	}, [isPlayableMediaFile, nxObjectUuid, subscribe, unsubscribe]);

	useEffect(() => {
		if (!isPlayableMediaFile || !isProxyJobRunning) return undefined;
		const interval = setInterval(() => refresh(nxObjectUuid), RUNNING_JOB_POLL_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [isPlayableMediaFile, isProxyJobRunning, nxObjectUuid, refresh]);

	if (!nxObjectFull) return undefined;

	const { proxy } = nxObjectFull.metadata;
	if (proxy) return proxy;
	// Until the jobs have loaded there is no telling a running job from none at all.
	if (areJobsLoading) return undefined;
	if (isProxyJobRunning) return { status: 'pending' };
	if (latestProxyJob?.status === 'FAILED' || latestProxyJob?.status === 'CANCELLED') {
		return { status: 'errored', reason: 'Mezz Proxy job failed' };
	}
	return { status: 'notAvailable' };
}
